
'use client';

import type { ArtItem } from '@/types';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, EyeOff } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { getArtworkImageUrl, shouldBypassNextImageOptimization } from '@/lib/utils';

interface ArtListProps {
  items: ArtItem[];
}

export function ArtList({ items }: ArtListProps) {
  return (
    <div className="rounded-md border">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-[100px]">Image</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Artist</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead className="text-right">Visibility</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map(item => {
                    const imageUrl = getArtworkImageUrl(item, 'sm');

                    return (
                    <TableRow key={item.id}>
                        <TableCell>
                            <Link href={`/artworks/${item.id}`}>
                                <div className="w-16 h-20 relative rounded-md overflow-hidden bg-muted">
                                    <Image 
                                        src={imageUrl}
                                        alt={item.title} 
                                        fill 
                                        sizes="64px"
                                        className="object-cover"
                                        unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                                    />
                                </div>
                            </Link>
                        </TableCell>
                        <TableCell className="font-medium">
                            <Link href={`/artworks/${item.id}`} className="hover:underline">
                                {item.title}
                            </Link>
                        </TableCell>
                        <TableCell>{item.artistNames?.join(', ')}</TableCell>
                        <TableCell>{item.year || 'N/A'}</TableCell>
                        <TableCell className="text-right">
                            {item.visibility === 'public' ? (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Badge variant="outline"><Eye className="mr-1 h-3 w-3" />Public</Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>Visible to everyone</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            ) : (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Badge variant="secondary"><EyeOff className="mr-1 h-3 w-3" />Private</Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>Only visible to you</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </TableCell>
                    </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </div>
  );
}
